import { MouseEvent, useEffect, useRef, useState } from 'react';
import { Popover } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import { CourseMappingDropdownOption } from './courseMappingDropdownOptions';
import {
  CourseMappingFilterField,
  CourseMappingFilterValue,
  getFilterValueSummary,
  isFilterApplied,
} from './courseMappingFilterFields';
import CourseMappingFilterValuePicker from './CourseMappingFilterValuePicker';

import './CourseMappingFilterChip.css';

interface CourseMappingFilterChipProps {
  field: CourseMappingFilterField;
  value: CourseMappingFilterValue;
  options: CourseMappingDropdownOption[];
  defaultOpen?: boolean;
  locked?: boolean;
  onCommit: (value: CourseMappingFilterValue) => void;
  onRemove: () => void;
}

const CourseMappingFilterChip = ({
  field,
  value,
  options,
  defaultOpen = false,
  locked = false,
  onCommit,
  onRemove,
}: CourseMappingFilterChipProps) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<CourseMappingFilterValue>(value);

  // Auto-open a freshly added chip so the user can configure it right away.
  useEffect(() => {
    if (defaultOpen) {
      setDraft(value);
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpen]);

  const applied = isFilterApplied(field, value);
  const summary = applied ? getFilterValueSummary(field, value, options) : '';

  const handleOpen = () => {
    setDraft(value);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    if (JSON.stringify(draft) !== JSON.stringify(value)) {
      onCommit(draft);
    }
  };

  const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onRemove();
  };

  const chipLabel = (
    <span className="course-mapping-filter-chip-label">
      <span className="course-mapping-filter-chip-name">
        {field.label}
        {applied ? ':' : ''}
      </span>
      {applied ? <span className="course-mapping-filter-chip-value"> {summary}</span> : null}
    </span>
  );

  if (locked) {
    return (
      <div className="course-mapping-filter-chip course-mapping-filter-chip--applied course-mapping-filter-chip--locked">
        <span className="course-mapping-filter-chip-main course-mapping-filter-chip-main--locked">{chipLabel}</span>
      </div>
    );
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={`course-mapping-filter-chip${applied ? ' course-mapping-filter-chip--applied' : ''}`}
      >
        <button
          type="button"
          className="course-mapping-filter-chip-main"
          onClick={handleOpen}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          {chipLabel}
          <ArrowDropDownIcon className="course-mapping-filter-chip-icon" fontSize="small" />
        </button>
        <button
          type="button"
          className="course-mapping-filter-chip-remove"
          aria-label={`Remove ${field.label} filter`}
          onClick={handleRemove}
        >
          <CloseIcon fontSize="inherit" />
        </button>
      </div>
      <Popover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ className: 'course-mapping-filter-chip-popover' }}
      >
        <CourseMappingFilterValuePicker field={field} value={draft} options={options} onChange={setDraft} />
      </Popover>
    </>
  );
};

export default CourseMappingFilterChip;
